import { useCallback, useMemo } from 'react';
import { useSyncCenterSettings } from 'src/hooks/api/useSyncCenterSetting';
import { getAllInPriceFromListPriceForListing } from 'src/utils/inventoryUtils';
import {
  Listing,
  ListingBroadcastStatus,
  ListingPriceCalculation,
} from 'src/WebApiController';

import { ComparableListing } from '../ComparableListings';
import {
  CompListingWithSeatScore,
  ListingWithSeatScore,
} from './ConnectedComparableListings';

const mapCompListingToComparableListing = (
  compListing: CompListingWithSeatScore,
  listingPriceCalc?: ListingPriceCalculation | null
) => {
  const isAnchor = listingPriceCalc?.udrRefLstIds?.includes(
    compListing.listingId
  );

  return {
    listingId: compListing.listingId,
    section: compListing.section ?? undefined,
    row: compListing.row ?? undefined,
    seatFr: compListing.seatFrom ?? undefined,
    seatTo: compListing.seatTo ?? undefined,
    price: compListing.sellerAllInPrice?.amt,
    priceDisp: compListing.sellerAllInPrice?.disp,
    currencyCode: compListing.currencyCode ?? undefined,
    listingNotes: compListing.listingNotes ?? undefined,
    sellerNetProceeds: compListing.sellerNetProceeds?.amt ?? undefined,
    isOutlier:
      compListing.isWithoutOutlier == null
        ? false
        : compListing.isWithoutOutlier,
    isAnchor: isAnchor,
    isYourListing: false,
    isFanInventory: Boolean(compListing.isFanInventory),
    seatScore: compListing.seatScore,
    availableTickets: compListing.availableTickets,
    validPurchaseQuantities: compListing.validPurchaseQuantities,
  } as ComparableListing;
};

const mapListingToComparableListing = (
  listing: ListingWithSeatScore,
  isSelectedListing: boolean,
  listingPriceCalc?: ListingPriceCalculation | null
): ComparableListing => {
  // Should use price from pricer for the selected listing, and default to listing list price
  const listPrice = getAllInPriceFromListPriceForListing(listing);
  const websitePrice = isSelectedListing
    ? (listingPriceCalc?.websitePrice?.amt ?? listPrice)
    : listPrice;
  const autoPricingResultType =
    listingPriceCalc?.autoPricingResultType ?? undefined;
  const autoPricingResultConstraintType =
    listingPriceCalc?.autoPricingConstraintType ?? undefined;
  return {
    listingId: listing.id,
    section: listing.seating.section,
    row: listing.seating.row ?? undefined,
    seatFr: listing.seating.seatFr ?? undefined,
    seatTo: listing.seating.seatTo ?? undefined,
    price: websitePrice,
    unitCost: listing.unitCst?.amt ?? undefined,
    currencyCode: listing.currency,
    listingNotes: listing.seatTraits ?? undefined,
    // Price assuming same fees as seller’s account
    sellerNetProceeds: listing.listPrice,
    seatScore: listing.seatScore,
    isYourListing: isSelectedListing,
    isListed: listing.brdcstStatus === ListingBroadcastStatus.Broadcast,
    availableTickets: listing.availQty,
    validPurchaseQuantities: listing.vldPurQty,
    autoPricingResultType,
    autoPricingResultConstraintType,
    shouldShowResultTypeAction: listing.isAutoPrc ?? false,
  };
};

export const useSellerListings = (
  selectedListingId: number,
  listings: Listing[],
  compListings: CompListingWithSeatScore[],
  listingPriceCalc?: ListingPriceCalculation | null
) => {
  const query = useSyncCenterSettings();
  const showUnbroadcastListingsInComps = useMemo(
    () => Boolean(query.data?.showUnbroadcastListingsInComps),
    [query.data?.showUnbroadcastListingsInComps]
  );

  const currentListing = useMemo(() => {
    return listings.find((l) => selectedListingId === l.id);
  }, [listings, selectedListingId]);

  const filterSellerListing = useCallback(
    (listing: Listing): boolean => {
      // Always include the current listing
      if (currentListing?.id === listing.id) {
        return true;
      }

      // Only include listings that are in sellerAccountPosListingIds
      if (!listingPriceCalc?.sellerAccountPosListingIds.includes(listing.id)) {
        return false;
      }

      // Exclude listings with zero availability
      if (listing.availQty <= 0) {
        return false;
      }

      // Exclude unbroadcast listings if the setting is off
      if (!showUnbroadcastListingsInComps) {
        return listing.brdcstStatus === ListingBroadcastStatus.Broadcast;
      }

      return true;
    },
    [
      currentListing?.id,
      listingPriceCalc?.sellerAccountPosListingIds,
      showUnbroadcastListingsInComps,
    ]
  );

  const comparableListings = useMemo(() => {
    return compListings.map((l) =>
      mapCompListingToComparableListing(l, listingPriceCalc)
    );
  }, [compListings, listingPriceCalc]);

  const sellerListings = useMemo(() => {
    return (
      listings
        .filter(filterSellerListing)
        .map((l) =>
          mapListingToComparableListing(
            l,
            l.id === currentListing?.id,
            listingPriceCalc
          )
        ) ?? []
    );
  }, [currentListing?.id, filterSellerListing, listingPriceCalc, listings]);
  return { sellerListings, comparableListings, currentListing };
};
